import setThemeForKey from '../actions/setThemeForKey';
import Theme from '../store/schema/Theme';
import ThemeLoadStatus from '../store/schema/ThemeLoadStatus';
import store from '../store/Store';
import { HttpReadyState, HttpStatusCode } from 'owa-http-status-codes';
import OutlookHostConfig from '../store/schema/OutlookHostConfig';
import { trace } from 'owa-trace';
import {
    getAdaptiveCardHostConfig,
    getAdaptiveCardCompactHostConfig,
} from '../utils/getHostConfig';

export default async function resolveTheme(themeName: string): Promise<Theme> {
    themeName = themeName.toLowerCase();

    const defaultTheme: Theme = {
        hostConfig: getAdaptiveCardHostConfig(),
        isResolved: true,
        stylesheetUrl: null,
        themeConfigUrl: null,
    };

    if (store.themeStore.loadStatus !== ThemeLoadStatus.LoadSucceeded) {
        return Promise.resolve(defaultTheme);
    }

    if (themeName === 'default') {
        return Promise.resolve(defaultTheme);
    } else if (themeName === 'compact') {
        return Promise.resolve({
            hostConfig: getAdaptiveCardCompactHostConfig(),
            isResolved: true,
            stylesheetUrl: null,
            themeConfigUrl: null,
        });
    } else {
        return getThemeFromStore(themeName)
            .then(theme => {
                return Promise.resolve(theme);
            })
            .catch(error => {
                trace.info('Error resolving theme: ' + error);
                return Promise.resolve(defaultTheme);
            });
    }
}

function getThemeFromStore(themeName: string): Promise<Theme> {
    return new Promise((resolve, reject) => {
        const loadStatus = store.themeStore.loadStatus;
        trace.info('Theme definitions load status: ' + loadStatus);

        // This structure will be changed once we change to 2 states for Theme loading.
        switch (loadStatus) {
            case ThemeLoadStatus.LoadSucceeded:
                const theme = store.themeStore.themeMapping[themeName];
                if (!theme) {
                    reject(`No theme matching name ${themeName} found in store`);
                } else if (theme.isResolved) {
                    resolve(theme);
                } else {
                    downloadTheme(themeName, theme.themeConfigUrl)
                        .then(downloadedTheme => {
                            resolve(downloadedTheme);
                        })
                        .catch(error => {
                            reject(error);
                        });
                }
                break;
            case ThemeLoadStatus.LoadFailed:
                reject('Could not resolve theme. Themes were not loaded');
                break;
        }
    });
}

function downloadTheme(themeName: string, themeConfigUrl: string): Promise<Theme> {
    return new Promise((resolve, reject) => {
        const request: XMLHttpRequest = new XMLHttpRequest();
        request.onreadystatechange = function () {
            if (request.readyState === HttpReadyState.Done) {
                if (request.status === HttpStatusCode.OK) {
                    try {
                        const themeConfig = JSON.parse(request.response);
                        const theme: Theme = {
                            themeConfigUrl: themeConfigUrl,
                            hostConfig: new OutlookHostConfig(themeConfig.hostConfig),
                            isResolved: true,
                            stylesheetUrl: themeConfig.stylesheet,
                        };

                        // cache the theme
                        setThemeForKey(themeName, theme);
                        resolve(theme);
                    } catch (exception) {
                        reject(new Error('downloadTheme: error parsing response json'));
                    }
                } else {
                    reject(new Error(`downloadTheme http error: received ${request.status}`));
                }
            }
        };

        request.ontimeout = function () {
            reject(new Error('downloadTheme timeout'));
        };

        request.open('GET', themeConfigUrl, true);
        request.timeout = 2000;
        request.send();
    });
}
